import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  CheckCircle,
  AlertTriangle,
  Eye,
  Search,
  DollarSign,
  User,
  FileText,
  Download,
  Upload,
  Plus,
  X,
  Loader,
  Save,
  CalendarDays
} from 'lucide-react';
import { formatCurrency, formatDate, paymentsAPI, bondsAPI, facilityAgentAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { BOND_STATUS } from '../../constants/subscription.constants';

// ================= Helper Functions (Hoisted Outside Component) =================
export function getIssuerDisplayName(issuer) {
  if (!issuer) return 'Unknown Issuer';
  if (typeof issuer === 'string') return issuer;
  if (typeof issuer === 'object') {
    if (issuer.companyName) return issuer.companyName;
    if (issuer.firstName && issuer.lastName) return `${issuer.firstName} ${issuer.lastName}`;
    if (issuer.name) return issuer.name;
  }
  return 'Unknown Issuer';
}

export function getIssuerEmail(issuer) {
  if (!issuer) return 'N/A';
  if (typeof issuer === 'object') {
    // Check if issuer has email field
    if (issuer.email) return issuer.email;
    // Check nested paths where email might be
    if (issuer.user && issuer.user.email) return issuer.user.email;
  }
  // If issuer is a string (ID or name), return N/A as we can't extract email
  return 'N/A';
}

export function getFrequencyDisplayValue(frequency) {
  const frequencyMap = {
    'monthly': 'Monthly',
    'quarterly': 'Quarterly',
    'semi_annually': 'Semi-Annually',
    'semi-annually': 'Semi-Annually',
    'annually': 'Annually',
    'annual': 'Annually',
    'at_maturity': 'At Maturity',
    'one_time': 'One Time'
  };
  if (!frequency) return 'Quarterly';
  const lowerFreq = frequency.toLowerCase();
  return frequencyMap[lowerFreq] || frequency;
}

export function getBondTypeIcon(bondType) {
  return bondType === 'Sukuk' ? '☪' : '🏛';
}

export function getStatusBadge(status, approvalStatus) {
  if (approvalStatus === 'approved') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        L2 Approved
      </span>
    );
  }
  if (approvalStatus === 'rejected') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <X className="w-3 h-3 mr-1" />
        Rejected
      </span>
    );
  }
  const statusConfig = {
    'Upcoming': { icon: Clock, bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
    'Pending Information': { icon: AlertTriangle, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
    'Pending Funding': { icon: Clock, bgColor: 'bg-orange-100', textColor: 'text-orange-800' },
    'Pending Payment': { icon: Clock, bgColor: 'bg-purple-100', textColor: 'text-purple-800' },
    'Payment Confirmed': { icon: CheckCircle, bgColor: 'bg-emerald-100', textColor: 'text-emerald-800' },
    'In Progress': { icon: Clock, bgColor: 'bg-indigo-100', textColor: 'text-indigo-800' },
    'Completed': { icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800' },
    'Failed': { icon: AlertTriangle, bgColor: 'bg-red-100', textColor: 'text-red-800' }
  };
  const config = statusConfig[status] || statusConfig['Upcoming'];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
      <Icon className="w-3 h-3 mr-1" />
      {status}
    </span>
  );
}

export function getApprovalStatusBadge(approvalStatus) {
  switch (approvalStatus) {
    case 'pending_l2':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending L2
        </span>
      );
    case 'approved':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          L2 Approved
        </span>
      );
    case 'rejected':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <X className="w-3 h-3 mr-1" />
          Rejected
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="w-3 h-3 mr-1" />
          {approvalStatus}
        </span>
      );
  }
}

// Simple audit log function (replace with real API/service if available)
function logAudit(action, details = {}) {
  // Example: send to backend or use a logging service
  // Replace this with a real API call if available
  if (window && window.console) {
    console.log('[AUDIT]', action, details);
  }
}




const CouponProfitDistribution = () => {
  // Auth and permissions (already imported and used at the app level)
  const { logout, getDisplayName, user } = useAuth();

  // UI/modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUploadPaymentModal, setShowUploadPaymentModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Bond/payment selection
  const [selectedBond, setSelectedBond] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedPaymentForUpload, setSelectedPaymentForUpload] = useState(null);
  const [selectedPaymentForApproval, setSelectedPaymentForApproval] = useState(null);

  // File upload states
  const [uploadFile, setUploadFile] = useState(null);
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [profitNotes, setProfitNotes] = useState('');
  const [paymentFile, setPaymentFile] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvStats, setCsvStats] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalPaymentAmount, setTotalPaymentAmount] = useState('');
  const [proofOfSettlementFile, setProofOfSettlementFile] = useState(null);
  const [rentasId, setRentasId] = useState('');
  const [submissionError, setSubmissionError] = useState(null);
  const [isSubmittingForApproval, setIsSubmittingForApproval] = useState(false);
  const [showUpdateDateModal, setShowUpdateDateModal] = useState(false);
  const [paymentToUpdateDate, setPaymentToUpdateDate] = useState(null);
  const [paymentDateUpdateValue, setPaymentDateUpdateValue] = useState('');
  const [updateDateError, setUpdateDateError] = useState(null);
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);
  
  // Import payment dates states
  const [bondsWithNullDates, setBondsWithNullDates] = useState([]);
  const [selectedBondForImport, setSelectedBondForImport] = useState('');
  const [importCsvFile, setImportCsvFile] = useState(null);
  const [importCsvData, setImportCsvData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  // Data states
  const [payments, setPayments] = useState([]);
  const [bonds, setBonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debug/preview states for Input Lumpsum calculation
  const [previewRows, setPreviewRows] = useState([]);
  const [previewError, setPreviewError] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  // Filter and tab states
  const [searchTerm, setSearchTerm] = useState('');
  const [bondTypeFilter, setBondTypeFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [activeView, setActiveView] = useState('list');

  // Approval/rejection states
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [approvalComments, setApprovalComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionComments, setRejectionComments] = useState('');

  // Pending approvals data
  const [pendingApprovalsData, setPendingApprovalsData] = useState([]);
  const [pendingApprovalsLoading, setPendingApprovalsLoading] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  useEffect(() => {
    if (!selectedPaymentForUpload) {
      setSubmissionError(null);
      setIsSubmittingForApproval(false);
      return;
    }
    setSubmissionError(null);
    setTotalPaymentAmount('');
    setProofOfSettlementFile(null);
    setRentasId('');
  }, [selectedPaymentForUpload]);

  // Fetch payments from API
  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call the payments API to get upcoming payments specifically
      const paymentsResponse = await paymentsAPI.getUpcomingPayments();

      // Call the bonds API to get all bonds
      const bondsResponse = await bondsAPI.getBonds({
        limit: 100,
        page: 1
      });

      if (paymentsResponse && paymentsResponse.data && paymentsResponse.data.success) {
        // getUpcomingPayments returns direct array, not paginated
        setPayments(paymentsResponse.data.data || []);
      } else {
        setPayments([]);
      }

      if (bondsResponse && bondsResponse.data && bondsResponse.data.success) {
        setBonds(bondsResponse.data.data?.bonds || []);
      } else {
        setBonds([]);
      }
    } catch (err) {
      setError(`Failed to fetch payment data: ${err.response?.data?.message || err.message}`);
      setPayments([]);
      setBonds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchPendingApprovalsCount();
  }, []);

  // Fetch pending approvals when switching to approvals tab
  useEffect(() => {
    if (activeView === 'pending-approvals') {
      fetchPendingApprovals();
    }
  }, [activeView]);



  // Payment data from real API
  const upcomingPayments = useMemo(() => {
    // Ensure payments is an array before sorting
    if (!Array.isArray(payments)) {
      console.warn('Payments is not an array:', payments);
      return [];
    }

    // Sort payments by payment date (nearest first)
    const sortedPayments = [...payments].sort((a, b) => {
      const dateA = new Date(a.paymentDate);
      const dateB = new Date(b.paymentDate);
      return dateA - dateB;
    });

    return sortedPayments;
  }, [payments]); // Only depend on payments

  // Filter payments based on search and filters
  const filteredPayments = useMemo(() => {
    return upcomingPayments.filter(payment => {
      // Only exclude payments that are pending L2 approval (not approved or rejected)
      if (payment.approvalStatus === 'pending_l2') {
        return false;
      }
      // Audit log for upload payment action
      logAudit('Upload Payment Initiated', {
        paymentId: payment?._id || payment?.id,
        bondId: payment?.bondId?._id || payment?.bondId,
        user: user?.email || user?.id,
        timestamp: new Date().toISOString(),
      });

      const bondData = payment.bondId || {};
      const bondName = bondData.bondName || '';
      const issuer = bondData.issuer || '';
      const bondId = bondData._id || payment.bondId;
      const productType = bondData.productType || '';
      const bondType = productType === 'SUKUK' ? 'Sukuk' : 'Conventional';

      const matchesSearch = !searchTerm ||
        bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getIssuerDisplayName(issuer).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (typeof bondId === 'string' ? bondId.toLowerCase().includes(searchTerm.toLowerCase()) : false);

      const matchesBondType = bondTypeFilter === 'all' ||
        (bondTypeFilter === 'fixed' && bondType === 'Conventional') ||
        (bondTypeFilter === 'islamic' && bondType === 'Sukuk');

      const matchesStatus = paymentStatusFilter === 'all' || payment.paymentStatus === paymentStatusFilter;

      // Audit log for L2 approval action
      logAudit('L2 Approval Submitted', {
        paymentId: selectedPaymentForApproval?._id || selectedPaymentForApproval?.id,
        bondId: selectedPaymentForApproval?.bondId?._id || selectedPaymentForApproval?.bondId,
        user: user?.email || user?.id,
        comments: approvalComments,
        timestamp: new Date().toISOString(),
      });
      return matchesSearch && matchesBondType && matchesStatus;
    });
  }, [upcomingPayments, searchTerm, bondTypeFilter, paymentStatusFilter]);

  // (Helper functions moved to module scope: getStatusBadge, getBondTypeIcon, etc.)

  // Fetch pending approvals count
  const fetchPendingApprovalsCount = async () => {
    try {
      const response = await paymentsAPI.getPendingPaymentApprovals({ page: 1, limit: 1 });
      if (response.data.success) {
        setPendingApprovalsCount(response.data.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching pending approvals count:', error);
    }
  };

  // Fetch pending approvals data
  const fetchPendingApprovals = async () => {
    try {
      setPendingApprovalsLoading(true);
      const response = await paymentsAPI.getPendingPaymentApprovals({ page: 1, limit: 50 });
      if (response.data.success) {
        setPendingApprovalsData(response.data.data.payments || []);
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    } finally {
      setPendingApprovalsLoading(false);
    }
  };

  // Handle L2 approval
  const handleApproveL2 = async (payment) => {
    setSelectedPaymentForApproval(payment);
    setShowApprovalModal(true);
  };

  // Handle rejection
  const handleRejectPayment = async (payment) => {
    setSelectedPaymentForApproval(payment);
    setShowRejectionModal(true);
  };

  // Submit L2 approval
  const handleSubmitApproval = async () => {
    try {
      const response = await paymentsAPI.approveL2PaymentDistribution(
        selectedPaymentForApproval._id,
        { comments: approvalComments }
      );

      if (response.data.success) {
        alert('Payment distribution L2 approved successfully!');
        setShowApprovalModal(false);
        setSelectedPaymentForApproval(null);
        setApprovalComments('');

        // Refresh data
        fetchPayments();
        fetchPendingApprovalsCount();
        if (activeView === 'pending-approvals') {
          fetchPendingApprovals();
        }
      }
    } catch (error) {
      alert('Error approving payment: ' + (error.response?.data?.message || error.message));
    }
  };

  // Submit rejection
  const handleSubmitRejection = async () => {
    try {
      const response = await paymentsAPI.rejectPaymentDistribution(
        selectedPaymentForApproval._id,
        {
          rejectionReason: rejectionReason,
          comments: rejectionComments
        }
      );

      if (response.data.success) {
        alert('Payment distribution rejected successfully!');
        setShowRejectionModal(false);
        setSelectedPaymentForApproval(null);
        setRejectionReason('');
        setRejectionComments('');

        // Refresh data
        fetchPayments();
        fetchPendingApprovalsCount();
        if (activeView === 'pending-approvals') {
          fetchPendingApprovals();
        }
      }
    } catch (error) {
      alert('Error rejecting payment: ' + (error.response?.data?.message || error.message));
    }
  };

  // Get approval status badge
  // (Issuer/frequency helper functions moved outside component)



  const handleUploadProfitInfo = (bond) => {
    setSelectedBond(bond);
    setShowUploadModal(true);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setUploadFile(file);
    } else {
      alert('Please upload a CSV file');
    }
  };

  const handleSupportingDocsUpload = (event) => {
    const files = Array.from(event.target.files);
    setSupportingDocs(prev => [...prev, ...files]);
  };

  const removeSupportingDoc = (index) => {
    setSupportingDocs(prev => prev.filter((_, i) => i !== index));
  };

  const downloadInvestorTemplate = (bond) => {
    // Generate CSV template based on real bond data
    const headers = ['Investor ID', 'Investor Name', 'Token Holding Amount', 'Whitelisted Wallet'];
    const mockData = [
      ['INV001', 'Pension Fund Malaysia', '50000000', '0x1234567890abcdef1234567890abcdef12345678'],
      ['INV002', 'Insurance Corp Bhd', '30000000', '0xabcdef1234567890abcdef1234567890abcdef12'],
      ['INV003', 'Investment Bank Ltd', '20000000', '0x567890abcdef1234567890abcdef1234567890ab']
    ];

    const csvContent = [headers, ...mockData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `investor_template_${bond.bondId}.csv`);
    a.style.visibility = 'hidden';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleSubmitProfitInfo = () => {
    if (!uploadFile || !selectedBond) {
      alert('Please upload a CSV file');
      return;
    }

    // In real implementation, would make API call to submit profit information

    // Reset modal state
    setShowUploadModal(false);
    setSelectedBond(null);
    setUploadFile(null);
    setSupportingDocs([]);
    setProfitNotes('');

    alert('Profit information submitted successfully!');
  };

  // Handle upload payment - show modal for file upload
  const handleUploadPayment = (payment) => {
    // Check if payment is already uploaded and pending L2 approval
    if (payment.approvalStatus === 'pending_l2') {
      alert('This payment has already been uploaded and is pending L2 approval.');
      return;
    }

    // Check if payment is already approved
    if (payment.approvalStatus === 'approved') {
      alert('This payment has already been approved and processed.');
      return;
    }

    // Check if payment is rejected
    if (payment.approvalStatus === 'rejected') {
      alert('This payment was rejected. Please contact the administrator.');
      return;
    }

    setSelectedPaymentForUpload(payment);
    setShowUploadPaymentModal(true);
  };

  const closeUploadPaymentModal = () => {
    setShowUploadPaymentModal(false);
    setSelectedPaymentForUpload(null);
    setTotalPaymentAmount('');
    setPaymentFile(null);
    setPaymentNotes('');
    setProofOfSettlementFile(null);
    setRentasId('');
    setSubmissionError(null);
    setIsSubmittingForApproval(false);
  };



  // Handle payment file upload
  const handlePaymentFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    setCsvErrors([]);
    setCsvStats(null);
    setShowPreview(false);
    setAllocations([]);

    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    try {
      const text = await file.text();
      console.log('CSV file content length:', text.length);
      console.log('CSV file first 200 characters:', text.substring(0, 200));

      const parsedData = await parsePaymentCSV(text);
      console.log('Parsed data:', parsedData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setUploadedFile(file);
      setAllocations(parsedData.allocations || []);
      setCsvErrors(parsedData.errors || []);
      setCsvStats(parsedData.stats || null);
      setShowPreview((parsedData.errors || []).length === 0);

    } catch (error) {
      clearInterval(progressInterval);
      console.error('CSV parsing error:', error);
      setCsvErrors(['Failed to read CSV file: ' + error.message]);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const parsePaymentCSV = async (csvText) => {
    // Handle empty or invalid CSV text
    if (!csvText || typeof csvText !== 'string') {
      return { allocations: [], errors: ['Invalid CSV file: File is empty or corrupted'], stats: null };
    }

    const lines = csvText.trim().split('\n');

    // Handle empty file
    if (lines.length === 0) {
      return { allocations: [], errors: ['CSV file is empty'], stats: null };
    }

    // Handle file with only headers or no headers
    if (lines.length === 1) {
      return { allocations: [], errors: ['CSV file contains only headers or is empty'], stats: null };
    }

    const headers = lines[0].split(',').map(h => h.trim());

    const requiredHeaders = [
      'Investor ID', 'Investor Name', 'Payment Amount', 'Wallet Address',
      'Percentage', 'Payment Type', 'Payment Reference', 'Payment Date'
    ];

    const errors = [];
    const allocations = [];
    let totalAmount = 0;
    let totalPercentage = 0;

    // Validate headers
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      errors.push(`Missing required headers: ${missingHeaders.join(', ')}`);
      return { allocations: [], errors, stats: null };
    }

    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = line.split(',').map(v => v.trim());
      if (values.length !== headers.length) {
        errors.push(`Row ${i + 1}: Incorrect number of columns`);
        continue;
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });

      // Validate data
      if (!row['Investor ID'] || !row['Investor Name']) {
        errors.push(`Row ${i + 1}: Missing Investor ID or Name`);
        continue;
      }

      const paymentAmount = parseFloat(row['Payment Amount']);
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        errors.push(`Row ${i + 1}: Invalid Payment Amount`);
        continue;
      }

      if (!row['Wallet Address'] || row['Wallet Address'].length < 10) {
        errors.push(`Row ${i + 1}: Invalid Wallet Address`);
        continue;
      }

      const percentage = parseFloat(row['Percentage']);
      if (isNaN(percentage) || percentage <= 0) {
        errors.push(`Row ${i + 1}: Invalid Percentage`);
        continue;
      }

      if (!['Profit Distribution', 'Coupon Payment'].includes(row['Payment Type'])) {
        errors.push(`Row ${i + 1}: Invalid Payment Type (must be 'Profit Distribution' or 'Coupon Payment')`);
        continue;
      }

      if (!row['Payment Reference']) {
        errors.push(`Row ${i + 1}: Missing Payment Reference`);
        continue;
      }

      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(row['Payment Date'])) {
        errors.push(`Row ${i + 1}: Invalid Payment Date format (use YYYY-MM-DD)`);
        continue;
      }

      allocations.push({
        investorId: row['Investor ID'],
        investorName: row['Investor Name'],
        paymentAmount: paymentAmount,
        walletAddress: row['Wallet Address'],
        percentage: percentage,
        paymentType: row['Payment Type'],
        paymentReference: row['Payment Reference'],
        paymentDate: row['Payment Date']
      });

      totalAmount += paymentAmount;
      totalPercentage += percentage;
    }

    // Validate number of investors matches bond data
    const expectedInvestors = selectedPaymentForUpload?.bondId?.numberOfInvestors || 0;
    if (expectedInvestors && allocations.length !== expectedInvestors) {
      errors.push(`Number of investors in CSV (${allocations.length}) must match the bond allocation (${expectedInvestors} investors)`);
    }

    // Validate totals
    if (Math.abs(totalPercentage - 100) > 0.01) {
      errors.push(`Total percentage must equal 100% (current: ${totalPercentage.toFixed(2)}%)`);
    }

    // Database validation - only if basic validation passes
    if (errors.length === 0 && allocations.length > 0) {
      try {
        // Get bondId - could be ObjectId, ISIN, or undefined
        const bondId = selectedPaymentForUpload?.bondId?._id || selectedPaymentForUpload?.bondId || 'unknown';

        console.log('🔍 Sending validation request with bondId:', bondId);

        const validationResponse = await facilityAgentAPI.validateInvestorWallet(allocations, bondId);

        if (validationResponse && validationResponse.data && validationResponse.data.results) {
          const dbValidationResults = validationResponse.data.results;

          // Add database validation errors
          if (dbValidationResults.invalid && Array.isArray(dbValidationResults.invalid)) {
            dbValidationResults.invalid.forEach(invalidRow => {
              if (invalidRow.errors && Array.isArray(invalidRow.errors)) {
                invalidRow.errors.forEach(error => {
                  errors.push(`Row ${invalidRow.rowNumber}: ${error}`);
                });
              }
            });
          }

          // Update allocations with validated data
          if (dbValidationResults.valid && Array.isArray(dbValidationResults.valid) && dbValidationResults.valid.length > 0) {
            // Replace allocations with validated data
            const validatedAllocations = dbValidationResults.valid.map(validRow => ({
              investorId: validRow.investorId,
              investorName: validRow.investorName,
              paymentAmount: parseFloat(validRow.paymentAmount) || 0,
              walletAddress: validRow.walletAddress,
              percentage: parseFloat(validRow.percentage) || 0,
              paymentType: validRow.paymentType,
              paymentReference: validRow.paymentReference,
              paymentDate: validRow.paymentDate,
              validatedData: validRow.validatedData
            }));

            // Only use validated allocations if all rows are valid
            if (!dbValidationResults.invalid || dbValidationResults.invalid.length === 0) {
              allocations.length = 0; // Clear existing allocations
              allocations.push(...validatedAllocations);

              // Recalculate totals from validated data
              totalAmount = validatedAllocations.reduce((sum, row) => sum + (parseFloat(row.paymentAmount) || 0), 0);
              totalPercentage = validatedAllocations.reduce((sum, row) => sum + (parseFloat(row.percentage) || 0), 0);
            }
          }
        } else {
          errors.push('Invalid response from database validation service');
        }
      } catch (validationError) {
        console.error('Database validation error:', validationError);
        // Don't fail completely if database validation fails - allow basic CSV validation to pass
        // but warn the user
        errors.push('Warning: Database validation failed. Basic CSV validation passed, but investor and wallet data could not be verified. Please check your connection and try again for full validation.');
      }
    }

    const stats = {
      totalRows: lines.length - 1,
      validRows: allocations.length,
      totalAmount: totalAmount || 0,
      totalPercentage: totalPercentage || 0
    };

    return { allocations, errors, stats };
  };

  const downloadPaymentTemplate = () => {
    const csvContent = [
      'Investor ID,Investor Name,Payment Amount,Wallet Address,Percentage,Payment Type,Payment Reference,Payment Date',
      'INV001,Pension Fund Malaysia,250000,0x1234567890abcdef1234567890abcdef12345678,25.00,Profit Distribution,TXN-2025-001,2025-07-22',
      'INV002,Insurance Corp Bhd,150000,0xabcdef1234567890abcdef1234567890abcdef12,15.00,Profit Distribution,TXN-2025-002,2025-07-22',
      'INV003,Investment Bank Ltd,100000,0x567890abcdef1234567890abcdef1234567890ab,10.00,Profit Distribution,TXN-2025-003,2025-07-22'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'payment_distribution_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmitDistributionForApproval = async () => {
    if (!selectedPaymentForUpload) return;

    const numericTotal = parseFloat(totalPaymentAmount);
    if (!numericTotal || numericTotal <= 0) {
      setSubmissionError('Please enter a valid total payment amount greater than 0.');
      return;
    }

    // Validate that at least one proof of settlement method is provided
    if (!proofOfSettlementFile && !rentasId?.trim()) {
      setSubmissionError('Please provide either a proof of settlement file or a RENTAS reference (or both) before submitting.');
      return;
    }

    try {
      setIsSubmittingForApproval(true);
      setSubmissionError(null);

      const formData = new FormData();
      formData.append('paymentId', selectedPaymentForUpload._id || selectedPaymentForUpload.id);
      formData.append('bondId', selectedPaymentForUpload.bondId?._id || selectedPaymentForUpload.bondId);
      formData.append('paymentDate', selectedPaymentForUpload.paymentDate);
      formData.append('totalPaymentAmount', numericTotal.toString());
      
      // Append proof of settlement file if provided
      if (proofOfSettlementFile) {
        formData.append('proofOfSettlement', proofOfSettlementFile, proofOfSettlementFile.name);
      }
      
      // Append RENTAS reference if provided
      if (rentasId?.trim()) {
        formData.append('rentasId', rentasId.trim());
      }

      await paymentsAPI.submitCouponDistributionDraft(selectedPaymentForUpload._id || selectedPaymentForUpload.id, formData);

      alert('Distribution submitted for approval. Another Facility Agent must approve before requests are generated.');
      closeUploadPaymentModal();
      fetchPayments();
    } catch (error) {
      console.error('Error submitting distribution for approval:', error);
      setSubmissionError(error.response?.data?.message || error.message || 'Failed to submit distribution for approval.');
    } finally {
      setIsSubmittingForApproval(false);
    }
  };

  // Normalize a raw payment from API (which contains nested bondId) into flattened shape
  const normalizePayment = (payment) => {
    if (!payment || typeof payment !== 'object') return payment;
    const bond = payment.bondId && typeof payment.bondId === 'object' ? payment.bondId : {};
    
    // Normalize totalPaymentAmount - handle Decimal128 format
    let normalizedTotalAmount = null;
    if (payment.totalPaymentAmount !== null && payment.totalPaymentAmount !== undefined) {
      if (typeof payment.totalPaymentAmount === 'number') {
        normalizedTotalAmount = payment.totalPaymentAmount;
      } else if (payment.totalPaymentAmount?.$numberDecimal) {
        normalizedTotalAmount = Number(payment.totalPaymentAmount.$numberDecimal);
      } else if (typeof payment.totalPaymentAmount === 'string') {
        normalizedTotalAmount = Number(payment.totalPaymentAmount) || 0;
      } else {
        normalizedTotalAmount = Number(payment.totalPaymentAmount || 0);
      }
    }
    
    return {
      ...payment,
      bondName: payment.bondName || bond.bondName || payment.bondId?.bondName,
      issuer: payment.issuer || getIssuerDisplayName(bond.issuer),
      bondType: payment.bondType || (bond.productType === 'SUKUK' || bond.productType === 'Sukuk' ? 'Sukuk' : 'Conventional'),
      principalOutstanding: payment.principalOutstanding ?? bond.principalOutstanding ?? bond.issueAmount ?? 0,
      maturityDate: payment.maturityDate || bond.maturityDate,
      issueDate: payment.issueDate || bond.issueDate,
      couponRate: payment.couponRate ?? bond.couponRate,
      currency: payment.currency || bond.currency || 'MYR',
      totalPaymentAmount: normalizedTotalAmount !== null ? normalizedTotalAmount : payment.totalPaymentAmount
    };
  };

  const getActionButton = (payment) => {
    // Check if payment is pending L2 approval - no action buttons
    if (payment.approvalStatus === 'pending_l2') {
      return (
        <div className="flex space-x-2">
          <button
            disabled
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-400 bg-gray-100 cursor-not-allowed"
          >
            <Clock className="w-3 h-3 mr-1" />
            Pending L2
          </button>
          <button
            onClick={() => {
              setSelectedPayment(normalizePayment(payment));
              setShowDetailsModal(true);
            }}
            className="btn-secondary btn-sm"
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </button>
        </div>
      );
    }

    // Check if payment is approved - show completed status
    if (payment.approvalStatus === 'approved') {
      return (
        <div className="flex space-x-2">
          <button
            disabled
            className="btn-secondary btn-sm"
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            L2 Approved
          </button>
          <button
            onClick={() => {
              setSelectedPayment(normalizePayment(payment));
              setShowDetailsModal(true);
            }}
            className="btn-secondary btn-sm"
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </button>
        </div>
      );
    }

    // Check if payment is rejected - show rejected status
    if (payment.approvalStatus === 'rejected') {
      return (
        <div className="flex space-x-2">
          <button
            disabled
            className="btn-secondary btn-sm"
          >
            <X className="w-3 h-3 mr-1" />
            Rejected
          </button>
          <button
            onClick={() => {
              setSelectedPayment(normalizePayment(payment));
              setShowDetailsModal(true);
            }}
            className="btn-secondary btn-sm"
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </button>
        </div>
      );
    }

    // Original logic for pending payment status
    if (payment.paymentStatus === 'Pending Payment') {
      const hasPaymentDate = !!payment.paymentDate;
      return (
        <div className="flex space-x-2">
          <button
            onClick={() => handleUploadPayment(payment)}
            
            className={`btn-primary btn-sm ${!hasPaymentDate ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <DollarSign className="w-3 h-3 mr-1" />
            Input Lumpsum
          </button>
          <button
            onClick={() => openUpdateDateModal(payment)}
            
            className={`btn-secondary btn-sm ${!hasPaymentDate ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <CalendarDays className="w-3 h-3 mr-1" />
            Update Date
          </button>
          <button
            onClick={() => {
              setSelectedPayment(normalizePayment(payment));
              setShowDetailsModal(true);
            }}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </button>
        </div>
      );
    }

    // Original logic for completed status
    if (payment.paymentStatus === 'Completed') {
      return (
        <div className="flex space-x-2">
          <button
            disabled
            className="btn-secondary btn-sm"
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </button>
          <button
            onClick={() => {
              setSelectedPayment(normalizePayment(payment));
              setShowDetailsModal(true);
            }}
            className="btn-secondary btn-sm"
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </button>
        </div>
      );
    }

    return (
      <button
        onClick={() => {
          setSelectedPayment(normalizePayment(payment));
          setShowDetailsModal(true);
        }}
        className="btn-secondary btn-sm"
      >
        <Eye className="w-3 h-3 mr-1" />
        View Details
      </button>
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setBondTypeFilter('all');
    setPaymentStatusFilter('all');
  };

  // Debug helper: calculate distribution preview for current Input Lumpsum without submitting
  const handlePreviewDistribution = async () => {
    if (!selectedPaymentForUpload) {
      setPreviewError('No payment selected for distribution.');
      return;
    }

    const paymentDate = selectedPaymentForUpload.paymentDate;
    if (!paymentDate) {
      setPreviewError('Payment date is required to calculate distribution. Please set a payment date first.');
      return;
    }

    const amount = parseFloat(totalPaymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPreviewError('Please enter a valid total payment amount before calculating.');
      return;
    }

    try {
      setPreviewError(null);
      setIsPreviewing(true);

      const payload = {
        bondId: selectedPaymentForUpload.bondId?._id || selectedPaymentForUpload.bondId,
        paymentDate,
        totalPaymentAmount: amount
      };

      const response = await paymentsAPI.previewCouponDistributionDraft(
        selectedPaymentForUpload._id || selectedPaymentForUpload.id,
        payload
      );

      const data = response?.data?.data || response?.data;
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setPreviewRows(rows);
      setPreviewError(rows.length === 0 ? 'No eligible holdings found for this bond at the snapshot date.' : null);
    } catch (err) {
      setPreviewError(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to calculate distribution preview.'
      );
      setPreviewRows([]);
    } finally {
      setIsPreviewing(false);
    }
  };

  // Fetch bonds with null payment dates
  const fetchBondsWithNullDates = async () => {
    try {
      const response = await paymentsAPI.getBondsWithNullPaymentDates();
      if (response.data.success) {
        setBondsWithNullDates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching bonds with null payment dates:', error);
      setBondsWithNullDates([]);
    }
  };

  // Handle import modal open
  useEffect(() => {
    if (showImportModal) {
      fetchBondsWithNullDates();
    }
  }, [showImportModal]);

  // Handle CSV file upload for import
  const handleImportCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    setImportCsvFile(file);
    setImportError(null);
    setImportCsvData([]);

    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      
      if (lines.length < 2) {
        throw new Error('CSV file must contain at least a header row and one data row');
      }

      // Parse header
      const headers = lines[0].split(',').map(h => h.trim());
      
      // Check for required columns
      const hasSeq = headers.includes('Seq.') || headers.includes('Seq');
      const hasPaymentDate = headers.includes('Payment Date') || headers.includes('PaymentDate');
      
      if (!hasSeq) {
        throw new Error('CSV must contain "Seq." or "Seq" column');
      }
      if (!hasPaymentDate) {
        throw new Error('CSV must contain "Payment Date" or "PaymentDate" column');
      }

      // Parse data rows
      const csvData = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values = line.split(',').map(v => v.trim());
        if (values.length !== headers.length) {
          throw new Error(`Row ${i + 1}: Incorrect number of columns`);
        }

        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });

        csvData.push(row);
      }

      if (csvData.length === 0) {
        throw new Error('No data rows found in CSV');
      }

      setImportCsvData(csvData);
    } catch (error) {
      console.error('CSV parsing error:', error);
      setImportError(error.message || 'Failed to parse CSV file');
      setImportCsvFile(null);
    }
  };

  // Handle import submission
  const handleImportPaymentDates = async () => {
    if (!selectedBondForImport) {
      setImportError('Please select a bond');
      return;
    }

    if (!importCsvFile || importCsvData.length === 0) {
      setImportError('Please upload a valid CSV file');
      return;
    }

    try {
      setIsImporting(true);
      setImportError(null);

      const response = await paymentsAPI.importPaymentDates(selectedBondForImport, importCsvData);

      if (response.data.success) {
        alert('Payment dates imported successfully!');
        setShowImportModal(false);
        setSelectedBondForImport('');
        setImportCsvFile(null);
        setImportCsvData([]);
        
        // Refresh payments
        fetchPayments();
      } else {
        setImportError(response.data.message || 'Failed to import payment dates');
      }
    } catch (error) {
      console.error('Error importing payment dates:', error);
      setImportError(error.response?.data?.message || error.message || 'Failed to import payment dates');
    } finally {
      setIsImporting(false);
    }
  };

  // Close import modal
  const closeImportModal = () => {
    setShowImportModal(false);
    setSelectedBondForImport('');
    setImportCsvFile(null);
    setImportCsvData([]);
    setImportError(null);
  };

  const openUpdateDateModal = (payment) => {
    setPaymentToUpdateDate(payment);
    setPaymentDateUpdateValue(formatDateForInput(payment.paymentDate));
    setUpdateDateError(null);
    setShowUpdateDateModal(true);
  };

  const closeUpdateDateModal = () => {
    setShowUpdateDateModal(false);
    setPaymentToUpdateDate(null);
    setPaymentDateUpdateValue('');
    setUpdateDateError(null);
  };

  const handleSubmitUpdateDate = async () => {
    if (!paymentToUpdateDate) return;
    if (!paymentDateUpdateValue) {
      setUpdateDateError('Please select a payment date.');
      return;
    }

    try {
      setIsUpdatingDate(true);
      setUpdateDateError(null);

      await paymentsAPI.updatePaymentDate(paymentToUpdateDate._id || paymentToUpdateDate.id, {
        paymentDate: paymentDateUpdateValue
      });

      alert('Payment date updated successfully.');
      closeUpdateDateModal();
      fetchPayments();
    } catch (error) {
      console.error('Error updating payment date:', error);
      setUpdateDateError(error.response?.data?.message || error.message || 'Failed to update payment date.');
    } finally {
      setIsUpdatingDate(false);
    }
  };

  // Download CSV template for payment dates
  const downloadPaymentDateTemplate = () => {
    const headers = [
      'Seq.',
      'Start Date',
      'End Date',
      'Payment Date',
      'Ex-Date',
      'Profit Rate (%)',
      'Tenor',
      'Entitle for Profit',
      'Payment Type',
      'Skip',
      'If Skip, Allow Profit on Profit (POP)',
      'Profit on Profit Rate (%)',
      'Fixing Date',
      'Reference Rate',
      'Adjustment Date'
    ];

    // Example data rows (6 rows as shown in the image)
    const exampleData = [
      ['1', '09/09/2025', '09/03/2026', '09/03/2026', '09/03/2026', '3.310', '181', 'Yes', 'Cash', '', 'No', '0.000', '', '0.000', '09/03/2026'],
      ['2', '09/03/2026', '09/09/2026', '09/09/2026', '09/09/2026', '3.310', '184', 'Yes', 'Cash', '', 'No', '0.000', '', '0.000', '09/09/2026'],
      ['3', '09/09/2026', '09/03/2027', '09/03/2027', '09/03/2027', '3.310', '181', 'Yes', 'Cash', '', 'No', '0.000', '', '0.000', '09/03/2027'],
      ['4', '09/03/2027', '09/09/2027', '09/09/2027', '09/09/2027', '3.310', '184', 'Yes', 'Cash', '', 'No', '0.000', '', '0.000', '09/09/2027'],
      ['5', '09/09/2027', '09/03/2028', '09/03/2028', '09/03/2028', '3.310', '182', 'Yes', 'Cash', '', 'No', '0.000', '', '0.000', '09/03/2028'],
      ['6', '09/03/2028', '08/09/2028', '08/09/2028', '08/09/2028', '3.310', '183', 'Yes', 'Cash', '', 'No', '0.000', '', '0.000', '08/09/2028']
    ];

    // Combine headers and data
    const csvContent = [headers, ...exampleData]
      .map(row => row.map(cell => {
        // Handle cells that might contain commas or quotes
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(','))
      .join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'payment_date_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };









  // Create payment schedule for a bond


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading State */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-b-2 border-blue-600 rounded-full animate-spin"></div>
              <span className="text-gray-700">Loading bond data...</span>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 mx-4 mt-4 border border-red-200 rounded-md bg-red-50">
          <div className="flex">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading bond data</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Coupon Management</h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage coupon payments to investors for both Conventional and Sukuks
              </p>
            </div>
            <button
              onClick={() => setShowImportModal(true)}
              
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Payment Date
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Search and Filters */}
        <div className="grid grid-cols-1 gap-4 mb-6 lg:grid-cols-5">
          {/* Search Card */}
          <div className="lg:col-span-3">
            <div className="flex items-center h-full p-4 bg-white border border-light-gray">
              <div className="relative w-full">
                <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                <input
                  type="text"
                  placeholder="Search by Bond Name, ISIN, or Issuer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Bond Type Filter Card */}
          <div className="lg:col-span-1">
            <div className="flex items-center h-full p-4 bg-white border border-light-gray">
              <div className="w-full">
                <select
                  value={bondTypeFilter}
                  onChange={(e) => setBondTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Bond Types</option>
                  <option value="fixed">Conventional</option>
                  <option value="islamic">Sukuk</option>
                </select>
              </div>
            </div>
          </div>

          {/* Payment Status Filter Card */}
          <div className="lg:col-span-1">
            <div className="flex items-center h-full p-4 bg-white border border-light-gray">
              <div className="w-full">
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="Upcoming">Upcoming</option>
                  <option value="Pending Information">Pending Information</option>
                  <option value="Pending Funding">Pending Funding</option>
                  <option value="Pending Payment">Pending Payment</option>
                  <option value="Payment Confirmed">Payment Confirmed</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveView('list')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeView === 'list'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                My Payment Uploads ({filteredPayments.length})
              </button>
              <button
                onClick={() => setActiveView('pending-approvals')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeView === 'pending-approvals'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Pending Approvals ({pendingApprovalsCount})
              </button>
            </nav>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            {activeView === 'list'
              ? `Showing ${filteredPayments.length} of ${upcomingPayments.length} upcoming payments`
              : `Showing ${pendingApprovalsData.length} pending approvals`
            }
          </p>
        </div>

        {/* Payments Table */}
        <div className="overflow-hidden overflow-x-auto bg-white rounded-lg shadow">
        <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Payment Uploads</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">
                    Payment Date
                  </th>
                  <th className="table-header-cell">
                    Bond Name / ISIN
                  </th>
                  <th className="table-header-cell">
                    Issuer
                  </th>
                  <th className="table-header-cell">
                    Bond Type
                  </th>
                  <th className="table-header-cell">
                    Total Payment Amount (MYR)
                  </th>
                  <th className="table-header-cell">
                    Payment Status
                  </th>
                  <th className="table-header-cell">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activeView === 'list' ? (
                  // My Payment Uploads view
                  filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                        <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium">No payments found</p>
                        <p className="text-sm">Try adjusting your search or filter criteria</p>
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((payment) => (
                      <tr key={payment._id || payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.paymentDate ? (
                              formatDate(payment.paymentDate)
                            ) : (
                              <span className="text-xs text-gray-500">Not Set</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {payment.bondId?.bondName || 'Unknown Bond'}
                            </div>
                            <div className="font-mono text-xs text-gray-500">
                              {payment.bondId?.isin || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{getIssuerDisplayName(payment.bondId?.issuer)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{payment.bondId?.productType === 'SUKUK' ? 'Sukuk' : 'Conventional'}</span>
                          {payment.bondId?.productType !== 'SUKUK' && (
                            <div className="text-xs text-gray-500">
                              Coupon: {payment.bondId?.couponRate || 0}%
                            </div>
                          )}
                          {payment.bondId?.productType === 'SUKUK' && payment.bondId?.couponRate && (
                            <div className="text-xs text-gray-500">
                              Rate: {payment.bondId?.couponRate || 0}%
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {(() => {
                              const total = typeof payment.totalPaymentAmount === 'number'
                                ? payment.totalPaymentAmount
                                : payment.totalPaymentAmount?.$numberDecimal
                                  ? Number(payment.totalPaymentAmount.$numberDecimal)
                                  : Number(payment.totalPaymentAmount || 0);
                              return total > 0 ? formatCurrency(total) : '-';
                            })()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(payment.paymentStatus, payment.approvalStatus)}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                          {getActionButton(payment)}
                        </td>
                      </tr>
                    ))
                  )
                ) : (
                  // Pending Approvals view
                  pendingApprovalsLoading ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                        <Loader className="w-8 h-8 mx-auto mb-4 text-gray-400 animate-spin" />
                        <p className="text-lg font-medium">Loading pending approvals...</p>
                      </td>
                    </tr>
                  ) : pendingApprovalsData.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                        <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium">No pending approvals</p>
                        <p className="text-sm">All payments have been processed</p>
                      </td>
                    </tr>
                  ) : (
                    pendingApprovalsData.map((payment) => (
                      <tr key={payment._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.paymentDate ? (
                              formatDate(payment.paymentDate)
                            ) : (
                              <span className="text-xs text-gray-500">Not Set</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {payment.bondId?.bondName || 'Unknown Bond'}
                            </div>
                            <div className="font-mono text-xs text-gray-500">
                              {payment.bondId?.isin || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{getIssuerDisplayName(payment.bondId?.issuer)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{payment.bondId?.productType === 'SUKUK' ? 'Sukuk' : 'Conventional'}</span>
                          {payment.bondId?.productType !== 'SUKUK' && (
                            <div className="text-xs text-gray-500">
                              Coupon: {payment.bondId?.couponRate || 0}%
                            </div>
                          )}
                          {payment.bondId?.productType === 'SUKUK' && payment.bondId?.couponRate && (
                            <div className="text-xs text-gray-500">
                              Rate: {payment.bondId?.couponRate || 0}%
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.totalPaymentAmount && payment.totalPaymentAmount > 0 ?
                              formatCurrency(payment.totalPaymentAmount) :
                              '-'
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getApprovalStatusBadge(payment.approvalStatus)}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                          <div className="flex space-x-2">
                            {(() => {
                              // Check if current user is the actual initiator/creator of the payment
                              // For sub-accounts, they should be able to approve payments from their parent account
                              // Only disable buttons if the current user is the exact creator of the payment

                              // Use user.id instead of user._id since that's how the user object is structured
                              const currentUserId = user?.id || user?._id;
                              const isInitiator = payment.uploadedBy?._id === currentUserId ||
                                payment.actualCreatedBy?._id === currentUserId;

                              return (
                                <>
                                  <button
                                    onClick={() => handleApproveL2(payment)}
                                    
                                    className={`inline-flex items-center px-3 py-1.5 border shadow-sm text-xs font-medium rounded ${isInitiator
                                      ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed opacity-50'
                                      : 'border-transparent text-white bg-green-600 hover:bg-green-700'
                                      }`}
                                    title={isInitiator ? 'Cannot approve your own request' : 'Approve L2'}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Approve L2
                                  </button>
                                  <button
                                    onClick={() => handleRejectPayment(payment)}
                                    
                                    className={`inline-flex items-center px-3 py-1.5 border shadow-sm text-xs font-medium rounded ${isInitiator
                                      ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed opacity-50'
                                      : 'border-transparent text-white bg-red-600 hover:bg-red-700'
                                      }`}
                                    title={isInitiator ? 'Cannot reject your own request' : 'Reject'}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Reject
                                  </button>
                                  {isInitiator && (
                                    <span className="ml-2 text-sm text-red-500">(Your Request)</span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Upload Profit Information Modal */}
      {showUploadModal && selectedBond && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Upload Profit Distribution Information
              </h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Bond Details */}
            <div className="p-4 mb-6 rounded-lg bg-gray-50">
              <h4 className="mb-2 text-sm font-medium text-gray-700">Bond Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Bond Name:</span>
                  <span className="ml-2 font-medium">{selectedBond.bondName}</span>
                </div>
                <div>
                  <span className="text-gray-600">Bond ID:</span>
                  <span className="ml-2 font-mono">{selectedBond.bondId}</span>
                </div>
                <div>
                  <span className="text-gray-600">Issuer:</span>
                  <span className="ml-2 font-medium">{selectedBond.issuer}</span>
                </div>
                <div>
                  <span className="text-gray-600">Principal Outstanding:</span>
                  <span className="ml-2 font-medium">{formatCurrency(selectedBond.principalOutstanding)}</span>
                </div>
              </div>
            </div>

            {/* Payment Period */}
            <div className="mb-6">
              <h4 className="mb-2 text-sm font-medium text-gray-700">Payment Period</h4>
              <div className="p-3 rounded-lg bg-blue-50">
                <span className="text-sm text-blue-900">
                  Distribution for Q4 2024: 01 Oct 2024 - 31 Dec 2024
                </span>
              </div>
            </div>

            {/* Download Investor List */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">Investor List Template</h4>
                <button
                  onClick={() => downloadInvestorTemplate(selectedBond)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download Investor List
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Download a CSV template pre-populated with the current list of investors for this bond
              </p>
            </div>

            {/* Upload Profit Distribution File */}
            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Upload Profit Distribution File *
              </label>
              <div className="p-4 border-2 border-gray-300 border-dashed rounded-lg">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="profit-file-upload"
                />
                <label
                  htmlFor="profit-file-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Upload className="w-8 h-8 mb-2 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Click to upload CSV file or drag and drop
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    Required columns: Investor ID, Profit Distribution Amount (MYR)
                  </span>
                </label>
                {uploadFile && (
                  <div className="p-2 mt-2 border rounded bg-green-50">
                    <span className="text-sm text-green-800">
                      ✓ {uploadFile.name}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Supporting Documents */}
            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Supporting Documents (Optional)
              </label>
              <input
                type="file"
                multiple
                onChange={handleSupportingDocsUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                Upload asset performance reports, profit calculation statements, etc.
              </p>
              {supportingDocs.length > 0 && (
                <div className="mt-2 space-y-1">
                  {supportingDocs.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 rounded bg-gray-50">
                      <span className="text-sm text-gray-700">{doc.name}</span>
                      <button
                        onClick={() => removeSupportingDoc(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Notes/Comments (Optional)
              </label>
              <textarea
                value={profitNotes}
                onChange={(e) => setProfitNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any internal notes regarding this distribution..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitProfitInfo}
                disabled={!uploadFile}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Upload & Validate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Payment Modal */}
      {showUploadPaymentModal && selectedPaymentForUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Input Lumpsum Payment Distribution for {selectedPaymentForUpload.bondName || selectedPaymentForUpload.bondId?.bondName || 'Unknown Bond'}
                </h3>
                <button
                  onClick={closeUploadPaymentModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Bond Summary */}
              <div className="p-4 mb-6 rounded-lg bg-gray-50">
                <h4 className="mb-3 text-sm font-medium text-gray-900">Bond Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Bond Name:</span>
                    <div className="font-medium">
                      {selectedPaymentForUpload.bondName || 
                       selectedPaymentForUpload.bondId?.bondName || 
                       'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Issuer:</span>
                    <div className="font-medium">
                      {selectedPaymentForUpload.issuer || 
                       getIssuerDisplayName(selectedPaymentForUpload.bondId?.issuer) || 
                       'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Payment Type:</span>
                    <div className="font-medium">
                      {selectedPaymentForUpload.paymentType || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Investors:</span>
                    <div className="font-medium">
                      {selectedPaymentForUpload?.bondId?.numberOfInvestors || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Payment Amount Input Section */}
              <div className="mb-6">
                <h4 className="mb-3 text-sm font-medium text-gray-900">Enter Total Payment Amount</h4>
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <p className="mb-3 text-xs text-blue-700">
                    Payment amounts will be automatically calculated based on each investor's allocation percentage from the bond allocation when you submit.
                  </p>
                  <div className="flex-1">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Total Payment Amount (MYR) *
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={totalPaymentAmount}
                        onChange={(e) => setTotalPaymentAmount(e.target.value)}
                        placeholder="Enter total payment amount"
                        className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                  </div>
                  </div>
                </div>
              </div>

              {/* Debug Preview Table */}
              {previewError && (
                <div className="p-3 mb-4 text-xs text-red-700 border border-red-200 rounded bg-red-50">
                  {previewError}
                </div>
              )}
              {previewRows && previewRows.length > 0 && (
                <div className="mb-6">
                  <h4 className="mb-2 text-sm font-medium text-gray-900">
                    Debug Preview: Distribution by Holder (T-1 Snapshot)
                  </h4>
                  <div className="overflow-x-auto border border-gray-200 rounded">
                    <table className="min-w-full text-xs divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 font-medium text-left text-gray-700">Holder</th>
                          <th className="px-3 py-2 font-medium text-left text-gray-700">Type</th>
                          <th className="px-3 py-2 font-medium text-right text-gray-700">Tokens</th>
                          <th className="px-3 py-2 font-medium text-right text-gray-700">% of Total</th>
                          <th className="px-3 py-2 font-medium text-right text-gray-700">Amount (MYR)</th>
                          <th className="px-3 py-2 font-medium text-left text-gray-700">Sub-Investors</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {(() => {
                          const totalTokens = previewRows.reduce(
                            (sum, r) => sum + (r.tokenAmount || 0),
                            0
                          ) || 1;
                          return previewRows.map((row, idx) => {
                            const holder = row.holderSnapshot || {};
                            const sharePct = (row.tokenAmount || 0) / totalTokens * 100;
                            const breakdown = Array.isArray(row.distributionBreakdown)
                              ? row.distributionBreakdown
                              : [];
                            return (
                              <tr key={idx}>
                                <td className="px-3 py-2">
                                  <div className="font-medium text-gray-900">
                                    {holder.name || holder.email || row.holderId || '—'}
                                  </div>
                                  <div className="text-gray-500">
                                    {holder.email || ''}
                                  </div>
                                </td>
                                <td className="px-3 py-2">
                                  {row.holderType === 'custodian' ? 'Custodian' : 'Direct'}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {(row.tokenAmount || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {sharePct.toFixed(4)}%
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {formatCurrency(row.amount || 0)}
                                </td>
                                <td className="px-3 py-2">
                                  {breakdown.length > 0 ? (
                                    <span className="text-gray-700">
                                      {breakdown.length} sub-investor(s)
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Proof of Settlement - File Upload or RENTAS reference */}
              <div className="mb-6">
                <h4 className="mb-3 text-sm font-medium text-gray-900">Proof of Settlement *</h4>
                <p className="mb-3 text-xs text-gray-500">
                  Provide either a proof of settlement file, RENTAS reference, or both. At least one is required.
                </p>
                
                {/* File Upload Section */}
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Upload File (Optional)
                  </label>
                  <div className="p-4 border-2 border-gray-300 border-dashed rounded-lg">
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.type !== 'application/pdf') {
                            alert('Please upload a PDF file only.');
                            e.target.value = '';
                            return;
                          }
                          if (file.size > 20 * 1024 * 1024) {
                            alert('File size must be less than 20MB.');
                            e.target.value = '';
                            return;
                          }
                          setProofOfSettlementFile(file);
                        }
                      }}
                      className="hidden"
                      id="proof-of-settlement-upload"
                    />
                    <label
                      htmlFor="proof-of-settlement-upload"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="w-8 h-8 mb-2 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Click to upload PDF file or drag and drop
                      </span>
                      <span className="mt-1 text-xs text-gray-500">
                        PDF only, max 20MB
                      </span>
                    </label>
                    {proofOfSettlementFile && (
                      <div className="flex items-center justify-between p-3 mt-3 border border-green-200 rounded bg-green-50">
                        <div className="flex items-center">
                          <FileText className="w-5 h-5 mr-2 text-green-600" />
                          <div>
                            <span className="text-sm font-medium text-green-800">
                              {proofOfSettlementFile.name}
                            </span>
                            <span className="ml-2 text-xs text-green-600">
                              ({(proofOfSettlementFile.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setProofOfSettlementFile(null);
                            document.getElementById('proof-of-settlement-upload').value = '';
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* RENTAS reference Input Section */}
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    RENTAS reference (Optional)
                  </label>
                  <input
                    type="text"
                    value={rentasId}
                    onChange={(e) => setRentasId(e.target.value)}
                    placeholder="Enter RENTAS reference"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the RENTAS transaction ID as proof of settlement.
                  </p>
                </div>

                {/* Validation message */}
                {!proofOfSettlementFile && !rentasId?.trim() && (
                  <p className="mt-2 text-xs text-red-600">
                    * At least one proof method (file upload or RENTAS reference) is required.
                  </p>
                )}
              </div>

              {submissionError && (
                <div className="p-4 mb-6 text-sm text-red-700 border border-red-200 rounded-md bg-red-50">
                  {submissionError}
                </div>
              )}

            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200">
              <div className="flex space-x-3">
                <button
                  onClick={closeUploadPaymentModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitDistributionForApproval}
                  disabled={
                    !totalPaymentAmount ||
                    parseFloat(totalPaymentAmount) <= 0 ||
                    (!proofOfSettlementFile && !rentasId?.trim()) ||
                    isSubmittingForApproval
                  }
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmittingForApproval ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Submit for Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showDetailsModal && selectedPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-medium text-gray-900">
                Payment Details 
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-xl text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Combined Bond & Payment Information - Full Width */}
              <div>
                <h4 className="pb-2 mb-4 text-base font-medium text-gray-900 border-b border-gray-200">Bond Details</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Bond Name:</span>
                    <span className="text-gray-900">{selectedPayment.bondName || selectedPayment.bondId?.bondName || 'Unknown Bond'}</span>
                  </div>
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Issuer:</span>
                    <span className="text-gray-900">{getIssuerEmail(selectedPayment.bondId?.issuer)}</span>
                  </div>
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Bond Type:</span>
                    <span className="text-gray-900">{selectedPayment.bondType}</span>
                  </div>
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">ISIN:</span>
                    <span className="font-mono text-sm text-gray-900">{selectedPayment.bondId?.isin || 'N/A'}</span>
                  </div>
                  {/* <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Principal Outstanding:</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(selectedPayment.principalOutstanding)}</span>
                  </div> */}
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Issue Date:</span>
                    <span className="text-gray-900">{selectedPayment.issueDate ? formatDate(selectedPayment.issueDate) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Maturity Date:</span>
                    <span className="text-gray-900">{selectedPayment.maturityDate ? formatDate(selectedPayment.maturityDate) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Payment Date:</span>
                    <span className="text-gray-900">{selectedPayment.paymentDate ? formatDate(selectedPayment.paymentDate) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Payment Type:</span>
                    <span className="text-gray-900">{selectedPayment.paymentType}</span>
                  </div>
               
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Payment Status:</span>
                    <span>{getStatusBadge(selectedPayment.paymentStatus)}</span>
                  </div>
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Currency:</span>
                    <span className="text-gray-900">{selectedPayment.currency}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div>
                <h4 className="pb-2 mb-4 text-base font-medium text-gray-900 border-b border-gray-200">Payment Information</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Coupon Rate:</span>
                    <span className="text-gray-900">{selectedPayment.bondId?.couponRate ? `${selectedPayment.bondId.couponRate}%` : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Coupon Frequency:</span>
                    <span className="text-gray-900">{getFrequencyDisplayValue(selectedPayment.bondId?.couponFrequency || selectedPayment.paymentFrequency)}</span>
                  </div>
                  {/* <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Profit Rate:</span>
                    <span className="text-gray-900">{selectedPayment.bondId?.couponRate ? `${selectedPayment.bondId.couponRate}%` : 'N/A'}</span>
                  </div> */}
                  <div className="flex justify-between md:flex-col md:justify-start">
                    <span className="mb-1 text-sm font-medium text-gray-600">Total Payment Amount:</span>
                    <span className="font-semibold text-gray-900">
                      {(() => {
                        const total = typeof selectedPayment.totalPaymentAmount === 'number'
                          ? selectedPayment.totalPaymentAmount
                          : selectedPayment.totalPaymentAmount?.$numberDecimal
                            ? Number(selectedPayment.totalPaymentAmount.$numberDecimal)
                            : Number(selectedPayment.totalPaymentAmount || 0);
                        return total > 0 ? formatCurrency(total) : '-';
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Proof of Settlement - Only show after approval */}
              {selectedPayment.approvalStatus === 'approved' && (() => {
                // Parse distribution data from payment notes
                let proofOfSettlement = null;
                try {
                  if (selectedPayment.notes) {
                    const parsedNotes = typeof selectedPayment.notes === 'string' 
                      ? JSON.parse(selectedPayment.notes) 
                      : selectedPayment.notes;
                    proofOfSettlement = parsedNotes?.distributionData?.proofOfSettlement || null;
                  }
                } catch (error) {
                  console.error('Error parsing payment notes:', error);
                }

                // Only show section if proof of settlement exists
                if (!proofOfSettlement || (!proofOfSettlement.url && !proofOfSettlement.rentasId)) {
                  return null;
                }

                return (
                  <div>
                    <h4 className="pb-2 mb-4 text-base font-medium text-gray-900 border-b border-gray-200">Proof of Settlement</h4>
                    <div className="space-y-3">
                      {/* Proof of Settlement File */}
                      {proofOfSettlement.url && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-600">Proof of settlement file:</span>
                            <span className="text-sm text-gray-900">{proofOfSettlement.filename || 'File'}</span>
                            {proofOfSettlement.size && (
                              <span className="text-xs text-gray-500">
                                ({(proofOfSettlement.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <a
                              href={proofOfSettlement.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-primary btn-sm"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Preview
                            </a>
                            <a
                              href={proofOfSettlement.url}
                              download={proofOfSettlement.filename || 'proof-of-settlement'}
                              className="btn-secondary btn-sm"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download
                            </a>
                          </div>
                        </div>
                      )}

                      {/* RENTAS reference */}
                      {proofOfSettlement.rentasId && (
                        <div className="flex items-center justify-between">
                           <span className="mb-1 text-sm font-medium text-gray-600">RENTAS reference</span>
                          <span className="font-mono text-gray-900">{proofOfSettlement.rentasId}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

          
            </div>

            {/* Action Buttons - Only Close button */}
            <div className="flex justify-end pt-6 mt-8 border-t border-gray-200">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedPaymentForApproval && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-2xl">
            <div className="flex items-center mb-4">
              <CheckCircle className="w-6 h-6 mr-3 text-green-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Approve L2 Payment Distribution
              </h3>
            </div>
            <div className="mb-4">
              <p className="mb-4 text-sm text-gray-600">
                You are about to approve the payment distribution for:
              </p>
              <div className="p-3 rounded-md bg-gray-50">
                <p className="text-sm font-medium text-gray-900">
                  {selectedPaymentForApproval.bondId?.bondName || 'Unknown Bond'}
                </p>
                <p className="text-xs text-gray-500">
                  Payment Date: {formatDate(selectedPaymentForApproval.paymentDate)}
                </p>
                <p className="text-xs text-gray-500">
                  Amount: {formatCurrency(selectedPaymentForApproval.totalPaymentAmount)}
                </p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Comments (Optional)
              </label>
              <textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Add any comments about this approval..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedPaymentForApproval(null);
                  setApprovalComments('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitApproval}
                className="px-4 py-2 text-sm font-medium text-white transition-colors bg-green-600 rounded-md hover:bg-green-700"
              >
                Approve L2
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Payment Dates Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Import CouponPayment Date
                </h3>
                <button
                  onClick={closeImportModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Download Template */}
              <div className="p-4 mb-6 border border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="mb-1 text-sm font-medium text-blue-900">
                      Need a template?
                    </h4>
                    <p className="text-xs text-blue-700">
                      Download the CSV template with the correct format and example data
                    </p>
                  </div>
                  <button
                    onClick={downloadPaymentDateTemplate}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </button>
                </div>
              </div>

              {/* Bond Selection */}
              <div className="mb-6">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Select Bond *
                </label>
                <select
                  value={selectedBondForImport}
                  onChange={(e) => setSelectedBondForImport(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">-- Select a bond --</option>
                  {bondsWithNullDates.map((bond) => (
                    <option key={bond._id} value={bond._id}>
                      {bond.bondName} ({bond.isin || 'N/A'}) - {bond.nullPaymentCount} payments need dates
                    </option>
                  ))}
                </select>
                {bondsWithNullDates.length === 0 && (
                  <p className="mt-2 text-sm text-gray-500">
                    No bonds found with null payment dates. All payment schedules have been configured.
                  </p>
                )}
              </div>

              {/* CSV Upload */}
              <div className="mb-6">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Upload CSV File *
                </label>
                <div className="p-4 border-2 border-gray-300 border-dashed rounded-lg">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportCsvUpload}
                    className="hidden"
                    id="import-csv-upload"
                  />
                  <label
                    htmlFor="import-csv-upload"
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <Upload className="w-8 h-8 mb-2 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Click to upload CSV file or drag and drop
                    </span>
                    <span className="mt-1 text-xs text-gray-500">
                      Required columns: Seq. (or Seq), Payment Date (or PaymentDate)
                    </span>
                  </label>
                  {importCsvFile && (
                    <div className="flex items-center justify-between p-3 mt-3 border border-green-200 rounded bg-green-50">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 mr-2 text-green-600" />
                        <div>
                          <span className="text-sm font-medium text-green-800">
                            {importCsvFile.name}
                          </span>
                          <span className="ml-2 text-xs text-green-600">
                            ({importCsvData.length} rows parsed)
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setImportCsvFile(null);
                          setImportCsvData([]);
                          document.getElementById('import-csv-upload').value = '';
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  CSV format: First row should contain headers (Seq., Payment Date). Each subsequent row should contain sequence number and payment date.
                </p>
              </div>

              {/* Error Display */}
              {importError && (
                <div className="p-4 mb-6 text-sm text-red-700 border border-red-200 rounded-md bg-red-50">
                  {importError}
                </div>
              )}

              {/* Preview */}
              {importCsvData.length > 0 && (
                <div className="mb-6">
                  <h4 className="mb-2 text-sm font-medium text-gray-700">
                    CSV Preview ({importCsvData.length} rows)
                  </h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-md max-h-60">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Seq.
                          </th>
                          <th className="px-3 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Payment Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {importCsvData.slice(0, 10).map((row, index) => (
                          <tr key={index}>
                            <td className="px-3 py-2 text-sm text-gray-900">
                              {row['Seq.'] || row['Seq']}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900">
                              {row['Payment Date'] || row['PaymentDate']}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importCsvData.length > 10 && (
                      <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
                        ... and {importCsvData.length - 10} more rows
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200">
              <div className="flex space-x-3">
                <button
                  onClick={closeImportModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportPaymentDates}
                  disabled={
                    !selectedBondForImport ||
                    !importCsvFile ||
                    importCsvData.length === 0 ||
                    isImporting
                  }
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isImporting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Payment Dates
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Payment Date Modal */}
      {showUpdateDateModal && paymentToUpdateDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-lg mx-4 bg-white rounded-lg shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Update Payment Date
                </h3>
                <button
                  onClick={closeUpdateDateModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Bond: <span className="font-medium text-gray-900">{paymentToUpdateDate.bondId?.bondName || 'Unknown Bond'}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Current Date:{' '}
                  {paymentToUpdateDate.paymentDate ? (
                    <span className="font-medium text-gray-900">{formatDate(paymentToUpdateDate.paymentDate)}</span>
                  ) : (
                    <span className="text-xs text-gray-500">Not Set</span>
                  )}
                </p>
              </div>

              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  New Payment Date *
                </label>
                <input
                  type="date"
                  value={paymentDateUpdateValue}
                  onChange={(e) => setPaymentDateUpdateValue(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {updateDateError && (
                <div className="p-3 mb-4 text-sm text-red-700 border border-red-200 rounded bg-red-50">
                  {updateDateError}
                </div>
              )}
            </div>

            <div className="flex justify-end px-6 py-4 space-x-3 border-t border-gray-200">
              <button
                onClick={closeUpdateDateModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitUpdateDate}
                disabled={!paymentDateUpdateValue || isUpdatingDate}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingDate ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Date
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedPaymentForApproval && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-2xl">
            <div className="flex items-center mb-4">
              <X className="w-6 h-6 mr-3 text-red-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Reject Payment Distribution
              </h3>
            </div>
            <div className="mb-4">
              <p className="mb-4 text-sm text-gray-600">
                You are about to reject the payment distribution for:
              </p>
              <div className="p-3 rounded-md bg-gray-50">
                <p className="text-sm font-medium text-gray-900">
                  {selectedPaymentForApproval.bondId?.bondName || 'Unknown Bond'}
                </p>
                <p className="text-xs text-gray-500">
                  Payment Date: {formatDate(selectedPaymentForApproval.paymentDate)}
                </p>
                <p className="text-xs text-gray-500">
                  Amount: {formatCurrency(selectedPaymentForApproval.totalPaymentAmount)}
                </p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Please provide a reason for rejection..."
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Additional Comments (Optional)
              </label>
              <textarea
                value={rejectionComments}
                onChange={(e) => setRejectionComments(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Add any additional comments..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setSelectedPaymentForApproval(null);
                  setRejectionReason('');
                  setRejectionComments('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRejection}
                disabled={!rejectionReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white transition-colors bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default CouponProfitDistribution;